<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { useShortcutDock } from 'dashboard/components-next/dock/useShortcutDock';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

defineProps({
  // SiteUp: na variação C o Resolver sai daqui e entra no pill com IA ativa/Pausar.
  showResolve: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { addConversation: addConversationToDock } = useShortcutDock();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const redirectToConversationList = () => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };

  router.push(
    conversationListPageURL({
      accountId: accountId.value,
      inboxId,
      label,
      teamId,
      conversationType: conversationTypeMap[name],
      customViewId,
    })
  );
};

const actionMenuItems = computed(() => {
  const items = [];

  // SiteUp V2 (Lucas 2026-08-10): fixar na barra rápida também pelos ⋮ da
  // conversa aberta — mesmo caminho do drop/clique-direito da lista.
  items.push({
    icon: 'i-ri-pushpin-line',
    label: t('CONVERSATION.CARD_CONTEXT_MENU.PIN_TO_DOCK'),
    action: 'pin_to_dock',
    value: 'pin_to_dock',
  });

  // SiteUp: "Marcar como não lida" num lugar VISÍVEL (antes só no clique-direito
  // do card). Útil pra sinalizar uma conversa pra voltar depois.
  items.push({
    icon: 'i-ri-mail-line',
    label: t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
    action: 'mark_unread',
    value: 'mark_unread',
  });

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-ri-volume-mute-line',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-ri-volume-down-line',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-ri-share-line',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  // Schedule message option moved to ContactPanel.vue

  return items;
});

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === 'pin_to_dock') {
    addConversationToDock({
      id: currentChat.value.id,
      label: currentChat.value?.meta?.sender?.name || '',
      thumbnail: currentChat.value?.meta?.sender?.thumbnail || '',
    });
    return;
  }

  if (action === 'mark_unread') {
    try {
      // Leave the open chat after unread — staying mounted keeps calling
      // markMessagesRead (SCROLL_TO_MESSAGE / sync) and undoes the API mark.
      await store.dispatch('markMessagesUnread', {
        id: currentChat.value.id,
      });
      emitter.emit('fetch_conversation_stats');
      redirectToConversationList();
    } catch {
      useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.MARK_AS_UNREAD_FAILED'));
    }
  } else if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      v-if="showResolve"
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-ri-more-2-line"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
